////////////////////////////////////////////////////////////////////////////////
//
//
// (C) Copyright 2023 Autodesk, Inc. All rights reserved.
//
//                      ****  CONFIDENTIAL MATERIAL  ****
//
// The information contained herein is confidential, proprietary to
// Autodesk, Inc., and considered a trade secret.  Use of this information
// by anyone other than authorized employees of Autodesk, Inc. is granted
// only under a written nondisclosure agreement, expressly prescribing the
// the scope and manner of such use.
//
////////////////////////////////////////////////////////////////////////////////

import React, {useReducer} from 'react';
import {Flyout} from "@adsk/alloy-react-tooltip";
import {ProjectUI} from "../dataModel/ProjectUI";
import FileStructureList from "./FileStructureList";
import ProjectDropdown from "./ProjectDropdown";
import {BIM360ItemBase} from "../dataModel/BIM360ItemBase";
import {FileUI} from "../dataModel/FileUI";
import {ZipTaskSetting} from "../dataModel/ZipTaskSetting";
import {FileTypeOptions, IsTabAvailableToUser, NormalizeFileExtension} from "../Utility";
import {
  CenteringContainer,
  ContentScroller,
  ContentWrapper,
  FlexColumn,
  FlexFill,
  FlexRowCentered,
  FolderBlueRecursive,
  FolderBlueSingle
} from '../CommonStyledComponents';
import Theme from "@adsk/alloy-react-theme";
import {AlertInformationIcon, FolderIcon} from "@adsk/alloy-react-icon";
import Illustration from "@adsk/alloy-react-illustration";
import Tooltip from '@adsk/alloy-react-tooltip';
import {FILE_TAB_IDS, FILE_TAB_TITLES, FILE_TABS} from "../Constants";
import Tabs, {Tab} from "@adsk/alloy-react-tabs";
import ProjectDropdownProjectWise from "./ProjectDropdownProjectWise";
import {ProjectWiseConfigurationUI} from "../dataModel/ProjectWiseConfigurationUI";
import {DirectoryUI} from "../dataModel/DirectoryUI";
import ProgressRing from "@adsk/alloy-react-progress-ring";
import {ClientProvider} from "../clients/ClientProvider";
import Panel from "@adsk/alloy-react-panel";
import {reducer} from "./reducers/SettingsSourceReducer";
import {SettingsSourceState} from "./states/SettingsSourceState";
import {SettingsSourceActions} from "../Enums";
import FilterButton from "@adsk/alloy-react-filter-button";
import MultiSelectDropdown from "./MultiSelectDropdown";

const authService = ClientProvider.AuthService;

const SettingsSource = (
  {
    projects,
    loadingProjects,
    selectedProject,
    projectWiseConfigs,
    loadingProjectWiseConfigs,
    retainCloudModels,
    files,
    directories,
    selectedProjectWiseConfig,
    getRootDirectories,
    getZipContents,
    getDirectoryContents,
    expandedIds,
    onExpandedChanged,
    onProjectSelected,
    onProjectWiseConfigSelected,
    onFileSelectionChanged,
    selectedTab,
    onTabChange,
    onError
  }: {
    projects: ProjectUI[],
    loadingProjects: boolean,
    selectedProject: ProjectUI | undefined,
    projectWiseConfigs: ProjectWiseConfigurationUI[],
    loadingProjectWiseConfigs: boolean,
    retainCloudModels: boolean,
    files: (FileUI | ZipTaskSetting)[],
    directories: DirectoryUI[],
    selectedProjectWiseConfig: ProjectWiseConfigurationUI | undefined,
    getRootDirectories: (rootObject: ProjectUI | ProjectWiseConfigurationUI) => Promise<DirectoryUI[]>
    getDirectoryContents: (directory: DirectoryUI) => Promise<void>,
    getZipContents: (file: FileUI) => Promise<void>,
    expandedIds?: string[],
    onExpandedChanged?: (expandedIds: string[]) => void,
    onProjectSelected?: (project: ProjectUI | undefined) => void,
    onProjectWiseConfigSelected?: (config: ProjectWiseConfigurationUI | undefined) => void,
    onFileSelectionChanged?: (currentSelection: {
      item: BIM360ItemBase,
      newIsSelected: boolean
    }[]) => void,
    selectedTab: string,
    onTabChange?: (newTab: string) => void,
    onError?: (error: any, operation: string) => void
  }) => {
  const [state, dispatch] = useReducer(reducer, new SettingsSourceState());

  function projectClick(project: ProjectUI | undefined): void {
    if (onExpandedChanged) {
      onExpandedChanged([]);
    }

    if (onProjectSelected) {
      onProjectSelected(project);
    }
  }

  const fileIds = files
    .filter(f => f instanceof FileUI)
    .map(f => (f as FileUI).Id) ?? [];
  const zipTasks = files
    .filter(f => f instanceof ZipTaskSetting) as ZipTaskSetting[];
  const zipIds: string[] = [];
  zipTasks.forEach(z => {
    z.Zips.forEach(zip => zipIds.push(`${zip.Id}_${zip.Name}`));
  });
  const directoryIds = directories.map(d => d.Id) ?? [];
  const selectionIds = fileIds.concat(directoryIds).concat(zipIds);

  return (
    <Panel.Container>
      <ContentScroller>
        <ContentWrapper>
          <FlexColumn>
            <FlexRowCentered>
              <h2 style={Theme.typography.heading2}>Source</h2>
              <Flyout trigger={Tooltip.TRIGGER_EVENTS.HOVER}
                      content={
                        <FlexColumn>
                          <FlexRowCentered style={{marginBottom: '1em'}}>
                            <FolderIcon style={{marginRight: '0.5em', flexShrink: 0}}/>
                            <div style={Theme.typography.bodyMedium}>Folder is not included in the task</div>
                          </FlexRowCentered>
                          <FlexRowCentered style={{marginBottom: '1em'}}>
                            <FolderBlueSingle style={{marginRight: '0.5em', flexShrink: 0}}/>
                            <div style={Theme.typography.bodyMedium}>Folder is included in the task</div>
                          </FlexRowCentered>
                          <FlexRowCentered>
                            <FolderBlueRecursive style={{marginRight: '0.5em', flexShrink: 0}}/>
                            <div style={Theme.typography.bodyMedium}>Folder and all nested folders are included in the
                              task
                            </div>
                          </FlexRowCentered>
                          <FlexRowCentered style={{...Theme.typography.bodyMedium, marginTop: '1em'}}>
                            Note that you can only select files and folders from one system (ProjectWise or Forge).
                            Selecting files and folders from one system will clear the others.
                          </FlexRowCentered>
                        </FlexColumn>
                      }>
                <AlertInformationIcon size={16} style={{marginLeft: '1em'}}/>
              </Flyout>
              <FlexFill/>
              {
                retainCloudModels &&
                <FlexRowCentered style={{marginRight: '1rem'}}>
                  <h4 style={Theme.typography.heading4}>Revit Version:</h4>
                  {
                    selectedProject?.RevitVersionLoaded &&
                    <h4 style={{
                      ...Theme.typography.heading4,
                      marginLeft: '0.5rem'
                    }}>{selectedProject?.RevitVersion ?? 'Unknown'}</h4>
                  }
                  {
                    selectedProject?.RevitVersionLoading &&
                    <ProgressRing size={'small'} style={{marginLeft: '0.5rem'}}/>
                  }
                </FlexRowCentered>
              }
              <FilterButton
                filtersOpen={state.FilterOpen}
                onClick={() => dispatch({type: SettingsSourceActions.filterOpen, payload: !state.FilterOpen})}
                clearFilters={() => null}/>
            </FlexRowCentered>
            <Tabs active={selectedTab}
                  style={{flex: 1, display: 'flex', flexDirection: "column", overflow: 'auto'}}
                  onChange={tab => {
                    if (onTabChange) {
                      onTabChange(tab);
                    }
                  }}>
              {Object.keys(FILE_TABS).map(k => {
                return !IsTabAvailableToUser(k, authService.CurrentUser) ? undefined : (
                  <Tab label={FILE_TAB_TITLES[k]}
                       tab={FILE_TAB_IDS[k]}
                       key={FILE_TAB_IDS[k]}
                       style={{flex: 1, display: 'flex', flexDirection: 'column', overflow: 'auto'}}>
                    <FlexColumn style={{paddingTop: '1em'}}>
                      {selectedTab === FILE_TAB_IDS[FILE_TABS.FORGE] &&
                        <>
                          {
                            (loadingProjects || projects.length > 0) &&
                            <div style={{width: '400px'}}>
                              <ProjectDropdown projects={projects}
                                               selectedProject={selectedProject}
                                               loadingProjects={loadingProjects}
                                               onSelected={projectClick}/>
                            </div>
                          }
                          {
                            !loadingProjects && projects.length === 0 &&
                            <CenteringContainer style={{flexDirection: 'column'}}>
                              <Illustration type={'folderEmptyGrey'} height={200} width={200}/>
                              <p style={Theme.typography.bodyLarge}>You don't have any projects available</p>
                            </CenteringContainer>
                          }
                          {
                            loadingProjects &&
                            <CenteringContainer style={{flex: 1}}>
                              <ProgressRing size={'large'}/>
                            </CenteringContainer>
                          }
                          <FileStructureList
                            rootObject={selectedProject}
                            getRootDirectories={getRootDirectories}
                            getZipContents={getZipContents}
                            fileTypeFilter={state.SelectedFileTypes.map(f => f.value)}
                            getDirectoryContents={getDirectoryContents}
                            onSelectionChange={onFileSelectionChanged}
                            allowSelection={true}
                            allowMultiSelection={true}
                            defaultSelection={selectionIds}
                            defaultExpansion={expandedIds}
                            onExpandedChange={onExpandedChanged}
                            onError={onError}/>
                        </>
                      }
                      {selectedTab === FILE_TAB_IDS[FILE_TABS.PROJECTWISE] &&
                        <>
                          {
                            (loadingProjectWiseConfigs || projectWiseConfigs.length > 0) &&
                            <ProjectDropdownProjectWise
                              projects={projectWiseConfigs}
                              selectedProject={selectedProjectWiseConfig}
                              loadingProjects={loadingProjectWiseConfigs}
                              onSelected={onProjectWiseConfigSelected}/>
                          }
                          {
                            !loadingProjectWiseConfigs && projectWiseConfigs.length === 0 &&
                            <CenteringContainer style={{flexDirection: 'column'}}>
                              <Illustration type={'folderEmptyGrey'} height={200} width={200}/>
                              <p style={Theme.typography.bodyLarge}>You don't have any ProjectWise Projects
                                available</p>
                            </CenteringContainer>
                          }
                          <FileStructureList
                            rootObject={selectedProjectWiseConfig}
                            getRootDirectories={getRootDirectories}
                            getZipContents={getZipContents}
                            getDirectoryContents={getDirectoryContents}
                            fileTypeFilter={state.SelectedFileTypes.map(f => f.value)}
                            onSelectionChange={onFileSelectionChanged}
                            allowSelection={true}
                            allowMultiSelection={true}
                            defaultSelection={selectionIds}
                            defaultExpansion={expandedIds}
                            onExpandedChange={onExpandedChanged}
                            onError={onError}/>
                        </>
                      }
                    </FlexColumn>
                  </Tab>
                )
              })}
            </Tabs>
          </FlexColumn>
        </ContentWrapper>
      </ContentScroller>
      <Panel
        title={'Filter Files'}
        open={state.FilterOpen}
        onClose={() => dispatch({type: SettingsSourceActions.filterOpen, payload: false})}>
        <Panel.Body>
          <div>
            <p>File Types</p>
            <MultiSelectDropdown
              items={FileTypeOptions}
              selected={state.SelectedFileTypes}
              itemNormalizationFunction={NormalizeFileExtension}
              onSelectionChange={selected => dispatch({
                type: SettingsSourceActions.selectedFileTypes,
                payload: selected
              })}/>
          </div>
        </Panel.Body>
      </Panel>
    </Panel.Container>
  );
};

export default SettingsSource;